// What an amendment does to a committed checklist
// (docs/specs/2026-09-28-task-checklist-design.md, decision 5) — ONE count,
// shown in the amend editor's footer before the save and written to the task's
// log and audit after it, so the two can never say different things.

import { EVIDENCE_KINDS, type IChecklistItemLike } from './gate.js';

export interface IChecklistDefinitionLike extends Omit<IChecklistItemLike, 'completion'> {
  content: string;
  order: number;
}

export interface IChecklistDelta {
  added: number;
  removed: number;
  /** Kept items whose words, kind or evidence modes changed. */
  changed: number;
  /** Kept items that now sit elsewhere among the kept ones. Renumbering after
   *  an addition or removal is not a move. */
  moved: number;
}

const DEFINITION = (item: IChecklistDefinitionLike): string =>
  JSON.stringify([item.kind, item.content, ...EVIDENCE_KINDS.map((kind) => item[kind])]);

const inOrder = <T extends IChecklistDefinitionLike>(items: readonly T[]): T[] => [...items].sort((a, b) => a.order - b.order);

export function checklistDelta(
  before: readonly IChecklistDefinitionLike[],
  after: readonly IChecklistDefinitionLike[],
): IChecklistDelta {
  const was = new Map(before.map((item) => [item.id, item] as const));
  const now = new Set(after.map((item) => item.id));
  const keptBefore = inOrder(before).filter((item) => now.has(item.id)).map((item) => item.id);
  const keptAfter = inOrder(after).filter((item) => was.has(item.id));
  return {
    added: after.filter((item) => !was.has(item.id)).length,
    removed: before.filter((item) => !now.has(item.id)).length,
    changed: keptAfter.filter((item) => DEFINITION(item) !== DEFINITION(was.get(item.id)!)).length,
    moved: keptAfter.filter((item, index) => keptBefore[index] !== item.id).length,
  };
}

/** An amendment that would change nothing. */
export const isNoopDelta = (delta: IChecklistDelta): boolean =>
  delta.added + delta.removed + delta.changed + delta.moved === 0;
